import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { HD_LANG_RECOMPUTE_ON_LOAD, HdContentLanguageService } from '@/app/human-design/chart/bodygraph/hd-content-language.service';
import { HdGateInfoService } from '@/app/human-design/chart/bodygraph/hd-gate-info.service';

/** De zes profiellijnen; 1-3 = persoonlijk (blauw), 4-6 = interpersoonlijk (donkerblauw). */
const HD_PROFILE_LINES: readonly number[] = [1, 2, 3, 4, 5, 6];
const HD_PROFILE_LINE_COLORS: Readonly<Record<number, string>> = {
  1: '#1983bf', 2: '#1983bf', 3: '#1983bf',
  4: '#1c658e', 5: '#1c658e', 6: '#1c658e',
};

/** Eén profiellijn-kaart + bijhorende kids-kaart in de content-taal. */
interface ProfileLineCardVm {
  readonly line: number;
  readonly color: string;
  readonly name: string;
  readonly keyword: string;
  readonly keyword2: string;
  readonly meta: string;
  /** HTML met `<b>`-accenten (via `[innerHTML]`). */
  readonly description: string;
  readonly plus: string;
  readonly minus: string;
  readonly kids: readonly string[];
}

/**
 * Pagina die de Profiel lijnen cheat sheet toont, nagebouwd uit het Figma-
 * design "A3 HD Profiles Cheat Sheet" (zonder A3-header/branding-footer).
 * Per lijn een profiellijn-kaart (beschrijving, nood-aan-lijst, schaduwlijst)
 * en een tips-voor-kids-kaart, in het raster van het origineel.
 *
 * Content komt uit de `human-design/profile-lines`-scope via
 * {@link HdGateInfoService.cheatSheetTextIn}; taalwissel via de gedeelde
 * content-taal-picker, nog niet vertaalde EN-velden vallen terug op NL.
 */
@Component({
  selector: 'hd-profile-lines-cheat-sheet-page',
  standalone: false,
  templateUrl: './hd-profile-lines-cheat-sheet-page.component.html',
  styleUrl: './hd-profile-lines-cheat-sheet-page.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HdProfileLinesCheatSheetPageComponent {
  private readonly gateInfo = inject(HdGateInfoService);
  private readonly contentLang = inject(HdContentLanguageService);

  private readonly lang = computed(() => this.contentLang.effectiveLang(), HD_LANG_RECOMPUTE_ON_LOAD);

  protected readonly pageTitle = computed(() => this.text('pageTitle'));
  protected readonly cardTitle = computed(() => this.text('cardTitle'));
  protected readonly kidsTitle = computed(() => this.text('kidsTitle'));
  protected readonly kidsIntro = computed(() => this.text('kidsIntro'));

  protected readonly cards = computed<readonly ProfileLineCardVm[]>(() =>
    HD_PROFILE_LINES.map(line => ({
      line,
      color: HD_PROFILE_LINE_COLORS[line],
      name: this.text(`lines.${line}.name`),
      keyword: this.text(`lines.${line}.keyword`),
      keyword2: this.text(`lines.${line}.keyword2`),
      meta: this.text(`lines.${line}.meta`),
      description: this.text(`lines.${line}.description`),
      plus: this.text(`lines.${line}.plus`),
      minus: this.text(`lines.${line}.minus`),
      kids: this.list(`lines.${line}.kids`),
    })),
  );

  constructor() {
    // Beide content-talen eager laden zodat de taalwissel én de NL-fallback
    // meteen correcte tekst opleveren.
    this.gateInfo.loadScopesForLang('nl');
    this.gateInfo.loadScopesForLang('en');
  }

  protected print(): void {
    window.print();
  }

  /** Scope-tekst in de content-taal, met NL-fallback voor (nog) lege EN-velden. */
  private text(key: string): string {
    const lang = this.lang();
    const value = this.gateInfo.cheatSheetTextIn('profileLines', key, lang);
    if (value !== '' || lang === 'nl') {
      return value;
    }
    return this.gateInfo.cheatSheetTextIn('profileLines', key, 'nl');
  }

  /** Scope-string-array in de content-taal, met dezelfde NL-fallback als {@link text}. */
  private list(key: string): readonly string[] {
    const lang = this.lang();
    const value = this.gateInfo.cheatSheetListIn('profileLines', key, lang);
    if (value.length > 0 || lang === 'nl') {
      return value;
    }
    return this.gateInfo.cheatSheetListIn('profileLines', key, 'nl');
  }
}
